import { useLocation } from 'react-router-dom'
import ROUTES from '../consts/paths'
import { getPagePermissions } from '../store/slices/roleSlice/selectors'

type PreparedRoutes = Record<string, string[]>

export enum PreparedRouteKeys {
  SlotEmailSettingsPage = 'Admin Settings - Slot Email Settings',
  ActivityMonitorPage = 'Admin Settings - Activity Monitor',
  AdminSettingsAcadUnits = 'Admin Settings - Acad Units',
  AdminSettingsUsers = 'Admin Settings - Users',
  AdminSettingsDistributionSeries = 'Admin Settings - Distribution Plans',
  AdminSettingsHierarchy = 'Admin Settings - Hierarchy',
  AdminSettingsTemplates = 'Admin Settings - Templates',
  ReportPage = 'Report - Page',
  StudentSurveyListPage = 'Student Survey List - Page',
  InstructorCourseListPage = 'Instructor Course List - Page',
  InstructorReportPage = 'Instructor Report - Page',
  TermsPage = 'Admin Settings - Terms',
  CourseCatalog = 'Admin Settings - Course Catalog',
  Attributes = 'Admin Settings - Attribute Management',
  GenericRoleSettings = 'Generic Role Settings - Survey Projects',
  AdminSettingsSurveyProject = 'Admin Settings - Survey Projects',
  PermissionManagement = 'Admin Settings - Permission Management',
  InstitutionalSettings = 'Admin Settings - Institutional Settings',
  AdminSettingsSetupWizard = 'Admin Settings - Setup Wizard',
  AdminSettingsIntegrations = 'Admin Settings - Emails',
  AdminSettingsSisSyncLogs = 'Admin Settings - SIS Sync Logs'
}

export type PreparedRouteKey = keyof typeof PreparedRouteKeys

export const preparedRoutes: PreparedRoutes = {
  [PreparedRouteKeys.AdminSettingsAcadUnits]: [ROUTES.ACADEMIC_UNITS],
  [PreparedRouteKeys.AdminSettingsUsers]: [ROUTES.STUDENTS, ROUTES.FACULTY_AND_STAFF],
  [PreparedRouteKeys.AdminSettingsDistributionSeries]: [ROUTES.DISTRIBUTION_SERIES],
  [PreparedRouteKeys.AdminSettingsHierarchy]: [ROUTES.REPORTING_HIERARCHY],
  [PreparedRouteKeys.AdminSettingsTemplates]: [ROUTES.SURVEYS, ROUTES.SURVEY_LIST],
  [PreparedRouteKeys.ReportPage]: [ROUTES.REPORTS, ROUTES.ADMIN_REPORTS, ROUTES.QR_CODE, ROUTES.SURVEY_PREVIEW],
  [PreparedRouteKeys.StudentSurveyListPage]: [ROUTES.STUDENT_SURVEYS, ROUTES.SURVEY_PAGE, ROUTES.SURVEY_COMPLETED, ROUTES.SAVED_COMMENTS],
  [PreparedRouteKeys.InstructorCourseListPage]: [ROUTES.INSTRUCTOR_SURVEYS, ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS, ROUTES.QR_CODE],
  [PreparedRouteKeys.InstructorReportPage]: [ROUTES.REPORTS, ROUTES.ADMIN_REPORTS, ROUTES.INSTRUCTOR_SURVEYS, ROUTES.QR_CODE, ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS],
  [PreparedRouteKeys.TermsPage]: [ROUTES.TERMS],
  [PreparedRouteKeys.CourseCatalog]: [ROUTES.COURSE_CATALOG],
  [PreparedRouteKeys.ActivityMonitorPage]: [ROUTES.TASKS],
  [PreparedRouteKeys.Attributes]: [ROUTES.ATTRIBUTES],
  [PreparedRouteKeys.GenericRoleSettings]: [ROUTES.REPORTS, ROUTES.ADMIN_VIEWER_DASHBOARD, ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS],
  [PreparedRouteKeys.AdminSettingsSurveyProject]: [ROUTES.COURSE_AND_ENROLLMENT, ROUTES.SURVEY_PREVIEW],
  [PreparedRouteKeys.PermissionManagement]: [ROUTES.PERMISSION_MANAGEMENT],
  [PreparedRouteKeys.InstitutionalSettings]: [ROUTES.INSTITUTION_SETTINGS],
  [PreparedRouteKeys.AdminSettingsIntegrations]: [ROUTES.EMAIL],
  [PreparedRouteKeys.AdminSettingsSisSyncLogs]: [ROUTES.SIS_SYNC_LOGS]
}

const isAllowedForPath = (path: string, pagePermissions: string[] | undefined): boolean => {
  if (!pagePermissions) return false
  return pagePermissions.some(permission => preparedRoutes?.[permission]?.some(routePath => path.startsWith(routePath)))
}

export const useAllowedPath = (pathname?: string): boolean => {
  const location = useLocation()
  const finalPathname = pathname ?? location.pathname
  const pagePermissions = getPagePermissions()
  return isAllowedForPath(finalPathname, pagePermissions)
}
